import React, { Suspense, lazy } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps, Switch } from 'react-router';
import { useAppSelector } from '../../@types/redux';
import { AnimatedLoader, IBaseUserRight, NoRightsScreen, Screen } from '@zz2/zz2-ui';
import { RightGrantsEnum } from '../../@types/enum/rightGrantsEnum';
import { DEFAULT_RIGHTS } from '../../appConstants';

const PrivateRoute = (props : RouteProps) : JSX.Element => {
    const session = useAppSelector(x => x.auth.session);
    const { component, ...rest } = props;

    const hasMenuRight = (url : string) : boolean => {
        if (!session) return false;
        if (session.user.userRights.length < 1) return false;

        const userRights : Array<IBaseUserRight> = session.user.userRights;
        if (userRights.length < 1) {
            return false;
        } else {
            const urlSections = url.split('/'); 

            if (urlSections[1] === 'stock' && urlSections[2] === 'transferRequests'
                || urlSections[1] === 'intake' && urlSections[2] === 'transferRequests') {
                return true;
            }

            const firstPartition = urlSections.slice(0, -1).join('/');
            const filteredUserRights : Array<IBaseUserRight> = userRights.filter(x => !!x.isActive && x.right.url === (urlSections[urlSections.length - 1].includes('Id') 
                ? firstPartition
                : url
            ));

            if (filteredUserRights.length > 0 && filteredUserRights.some(x => x.rightGrantLevel === RightGrantsEnum.View)) {
                return true;
            } else {
                return false;
            }
        }
    };

    const render = (routerProps : RouteComponentProps<any>) : React.ReactNode => {
        if (session) {
            if (!component) return;

            const Component = component;
            if (!hasMenuRight(routerProps.match.url) && DEFAULT_RIGHTS.some(x => x !== routerProps.match.url)) {
                return <NoRightsScreen openSupportTicket={() => console.log('Not implemented.')}/>;
            }
            
            return <Component {...routerProps} />;
        }

        return <Redirect to={{ pathname: '/login', state: { from: routerProps.location } }} />;
    };

    return (<Route {...rest} render={render} />);
};

{/* Route Imports */}
{/* Home */}
const Home = lazy(() => import('../home/Home'));
const FrequentlyAskedQuestionScreen = lazy(() => import('../home/FrequentlyAskedQuestionScreen'));

{/* Intake */}
const IntakeManager = lazy(() => import('../intake/transactions/IntakeManager'));
const IntakeTransferRequestManager = lazy(() => import('../intake/transferRequests/IntakeTransferRequestManager'));

{/* Stock */}
const StockManager = lazy(() => import('../stock/transactions/StockManager'));
const StockTransferWizard = lazy(() => import('../stock/wizard/StockTransferWizard'));
const StockTransferRequestManager = lazy(() => import('../stock/transferRequests/StockTransferRequestManager'));

{/* Transfer */}
const TransferManager = lazy(() => import('../transfer/TransferManager'));

{/* Order */}
const OrderManager = lazy(() => import('../order/OrderManager'));

{/* Dispatch */}
const DispatchManager = lazy(() => import('../dispatch/DispatchManager'));

{/* Trip */}
const TripManager = lazy(() => import('../trip/TripManager'));

{/* Rights */}
const UserManager = lazy(() => import('../right/userManagement/UserManager'));
const RightsManagement = lazy(() => import('../right/rightsManagement/RightsManager'));

{/* Master Data */}
const MasterDataHome = React.lazy(() => import('../masterData/masterDataHome'));
const AccountManager = React.lazy(() => import('../masterData/account/AccountManager'));
const IdentifierRangeManager = React.lazy(() => import('../masterData/identifierRange/IdentifierRangeManager'));
const EmployeeManager = React.lazy(() => import('../masterData/auth/employee/EmployeeManager'));
const EmployeeCardManager = React.lazy(() => import('../masterData/auth/employeeCard/EmployeeCardManager'));
const LocationManager = React.lazy(() => import('../masterData/location/LocationManager'));
const LocationLineTypeManager = React.lazy(() => import('../masterData/locationLineType/LocationLineTypeManager'));
const MethodManager = React.lazy(() => import('../masterData/method/MethodManager'));
const NurseryManager = React.lazy(() => import('../masterData/nursery/NurseryManager'));
const ProductManager = React.lazy(() => import('../masterData/product/ProductManager'));
const ProductTypeManager = React.lazy(() => import('../masterData/productType/ProductTypeManager'));
const ProfileManager = React.lazy(() => import('../masterData/profile/ProfileManager'));
const ReasonManager = React.lazy(() => import('../masterData/reason/ReasonManager'));
const SupplierManager = React.lazy(() => import('../masterData/supplier/SupplierManager'));
const UnitOfMeasureManager = React.lazy(() => import('../masterData/unitOfMeasure/UnitOfMeasureManager'));
const UnitOfMeasureTypeManager = React.lazy(() => import('../masterData/unitOfMeasureType/UnitOfMeasureTypeManager'));
const FleetManager = React.lazy(() => import('../masterData/fleet/FleetManager'));
const CustomerManager = React.lazy(() => import('../masterData/customer/CustomerManager'));

{/* FARM Master Data */}
const DivisionManager = lazy(() => import('../masterData/FARM/division/DivisionManager'));
const SubdivisionManager = lazy(() => import('../masterData/FARM/subdivision/SubdivisionManager'));
const DepartmentManager = lazy(() => import('../masterData/FARM/department/DepartmentManager'));
const FieldManager = lazy(() => import('../masterData/FARM/field/FieldManager'));
const BlockManager = lazy(() => import('../masterData/FARM/block/BlockManager'));
const CommodityManager = lazy(() => import('../masterData/FARM/commodity/CommodityManager'));
const VarietyManager = lazy(() => import('../masterData/FARM/variety/VarietyManager'));
const RootstockManager = lazy(() => import('../masterData/FARM/rootstock/RootstockManager'));
const ProjectManager = lazy(() => import('../masterData/FARM/project/ProjectManager'));
const CropVarietyManager = lazy(() => import('../masterData/FARM/cropVariety/CropVarietyManager'));

{/* Development Tools */}
const LogManager = React.lazy(() => import('../devTools/logging/LogManager'));
const ContactUsManager = React.lazy(() => import('../devTools/contactUs/ContactUsManager'));
const NewsManager = React.lazy(() => import('../devTools/news/NewsManager'));
const FrequentlyAskedQuestionManager = React.lazy(() => import('../devTools/frequentlyAskedQuestion/FrequentlyAskedQuestionManager'));


const Routes = () : React.ReactElement => {
    return (
        <Suspense fallback={<Screen><AnimatedLoader/></Screen>}>
            <Switch>
                <Route
                    path={'/'} exact
                    render={() => <Redirect from={'/'} to={{ pathname: '/home' }} />}
                />
                
                <PrivateRoute exact path='/home' component={Home} />
                <PrivateRoute exact path='/faq' component={FrequentlyAskedQuestionScreen} />

                {/* Intake */}
                <PrivateRoute exact path='/intake/transactions' component={IntakeManager} />
                <PrivateRoute exact path='/intake/transferRequests' component={IntakeTransferRequestManager} />
                <PrivateRoute exact path='/intake/transferRequests/:guid' component={IntakeTransferRequestManager} />

                {/* Stock */}
                <PrivateRoute exact path='/stock/transactions' component={StockManager} />
                <PrivateRoute exact path='/stock/wizard' component={StockTransferWizard} />
                <PrivateRoute exact path='/stock/transferRequests' component={StockTransferRequestManager} />
                <PrivateRoute exact path='/stock/transferRequests/:guid' component={StockTransferRequestManager} />

                {/* Transfer */}
                <PrivateRoute exact path='/transfer/transactions' component={TransferManager} />

                {/* Order */}
                <PrivateRoute exact path='/order/transactions' component={OrderManager} />

                {/* Dispatch */}
                <PrivateRoute exact path='/dispatch/transactions' component={DispatchManager} />

                {/* Trip */}
                <PrivateRoute exact path='/dispatch/trips' component={TripManager} />

                {/* Master Data */}
                <PrivateRoute exact path='/masterData' component={MasterDataHome} />
                <PrivateRoute exact path='/masterData/account' component={AccountManager} />
                <PrivateRoute exact path='/masterData/identifierRange' component={IdentifierRangeManager} />
                <PrivateRoute exact path='/masterData/employee' component={EmployeeManager} />
                <PrivateRoute exact path='/masterData/employeeCard' component={EmployeeCardManager} />
                <PrivateRoute exact path='/masterData/location' component={LocationManager} />
                <PrivateRoute exact path='/masterData/locationLineType' component={LocationLineTypeManager} />
                <PrivateRoute exact path='/masterData/method' component={MethodManager} />
                <PrivateRoute exact path='/masterData/nursery' component={NurseryManager} />
                <PrivateRoute exact path='/masterData/product' component={ProductManager} />
                <PrivateRoute exact path='/masterData/productType' component={ProductTypeManager} />
                <PrivateRoute exact path='/masterData/profile' component={ProfileManager} />
                <PrivateRoute exact path='/masterData/reason' component={ReasonManager} />
                <PrivateRoute exact path='/masterData/supplier' component={SupplierManager} />
                <PrivateRoute exact path='/masterData/unitOfMeasure' component={UnitOfMeasureManager} />
                <PrivateRoute exact path='/masterData/unitOfMeasureType' component={UnitOfMeasureTypeManager} />
                <PrivateRoute exact path='/masterData/fleet' component={FleetManager} />
                <PrivateRoute exact path='/masterData/customer' component={CustomerManager} />

                {/* FARM Master Data */}
                <PrivateRoute exact path='/masterData/division' component={DivisionManager} />
                <PrivateRoute exact path='/masterData/subdivision' component={SubdivisionManager} />
                <PrivateRoute exact path='/masterData/department' component={DepartmentManager} />
                <PrivateRoute exact path='/masterData/field' component={FieldManager} />
                <PrivateRoute exact path='/masterData/block' component={BlockManager} />
                <PrivateRoute exact path='/masterData/commodity' component={CommodityManager} />
                <PrivateRoute exact path='/masterData/variety' component={VarietyManager} />
                <PrivateRoute exact path='/masterData/rootstock' component={RootstockManager} />
                <PrivateRoute exact path='/masterData/project' component={ProjectManager} />
                <PrivateRoute exact path='/masterData/cropVariety' component={CropVarietyManager} />

                {/* Rights */}
                <PrivateRoute exact path='/rights/userManagement' component={UserManager} />
                <PrivateRoute exact path='/rights/rightsManagement' component={RightsManagement} />
                
                {/* Development Tools */}
                <PrivateRoute exact path='/development/tools' component={LogManager} />
                <PrivateRoute exact path='/development/contactUs' component={ContactUsManager} />
                <PrivateRoute exact path='/development/news' component={NewsManager} />
                <PrivateRoute exact path='/development/faq' component={FrequentlyAskedQuestionManager} />

            </Switch>
        </Suspense>
    );
};

export default Routes;